import styles from "./wysiwyg.module.scss";
import DOMPurify from "isomorphic-dompurify";
import { useIsMobile } from "./../../../../hooks/useIsMobile";

export interface WYSIWYGTextProps {
  /**
   * The format for the text. If set to full_html dangerouslySetInnerHTML will be used.
   * If set to filtered_html text will be filtered and sanitized before using dangerouslySetInnerHTML.
   */
  format?: "full_html" | "filtered" | "filtered_html" | null;
  /**
   * The content to be displayed inside.
   */
  content: string;
}

const ALLOWED_TAGS = [
  "a",
  "em",
  "strong",
  "cite",
  "blockquote",
  "ul",
  "ol",
  "li",
  "dl",
  "dt",
  "dd",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "img",
  "p",
  "table",
  "caption",
  "tbody",
  "thead",
  "tfoot",
  "th",
  "td",
  "tr",
  "#text",
  "br",
  "i",
];

const ALLOWED_ATTR = [
  "href",
  "hreflang",
  "cite",
  "type",
  "start",
  "id",
  "src",
  "alt",
  "data-entity-type",
  "data-entity-uuid",
  "target",
];

/**
 * Renders full HTML Content or filtered HTML content dependin on format. Use with care.
 * Full HTML could be mean potential XSS scripting vulnerability if the provider of the content
 * is not trusted (admin) don't use this component.
 */
export const WYSIWYGText = ({ format = null, content }: WYSIWYGTextProps) => {
  const isMobile = useIsMobile();

  switch (format) {
    case "filtered_html":
      const filteredContent = DOMPurify.sanitize(content, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        KEEP_CONTENT: false,
        ALLOW_DATA_ATTR: false,
      });
      return (
        <div
          className={styles.wysiwyg}
          data-testid="filtered-rendered-markup"
          dangerouslySetInnerHTML={{
            __html: isMobile ? filteredContent.replace(/&nbsp;/g, ' ') : filteredContent,
          }}
        />
      );
    case "full_html":
      const fullContent = DOMPurify.sanitize(content, {
        ADD_TAGS: ['iframe'],
        // Add additional attributes to DOMPurify defaults.
        ADD_ATTR: ["target"],
      });
      return (
        <div
          className={styles.wysiwyg}
          data-testid="full-rendered-markup"
          dangerouslySetInnerHTML={{
            __html: isMobile ? fullContent.replace(/&nbsp;/g, ' ') : fullContent,
          }}
        />
      );
    default:
      return <>{content}</>;
  }
};
